/**
 * PortfolioBreakdown - Displays grid of portfolio cards for Client Level View
 * TASK-206: Client Level View - Multi-Portfolio Comparison
 *
 * Shows all portfolios belonging to a client with key metrics and alerts
 * Click on a card to drill down to portfolio-level view
 */

import React from 'react';
import { Briefcase, AlertCircle, ChevronRight, Users } from 'lucide-react';
import { PortfolioInfo } from '../../hooks/useClientLevelData';
import TrendIndicator from './atoms/TrendIndicator';
import Badge from './atoms/Badge';

export interface PortfolioBreakdownProps {
  portfolios: PortfolioInfo[];
  onPortfolioClick: (portfolioId: string) => void;
  className?: string;
}

const PortfolioBreakdown: React.FC<PortfolioBreakdownProps> = ({
  portfolios,
  onPortfolioClick,
  className = ''
}) => {
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('de-CH', {
      style: 'currency',
      currency: 'CHF',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const getRiskBadgeColor = (riskProfile: string | number | null): 'default' | 'success' | 'warning' | 'error' | 'info' | 'neutral' => {
    if (!riskProfile) return 'default';
    if (typeof riskProfile === 'string') {
      const lower = riskProfile.toLowerCase();
      if (lower.includes('conservative') || lower.includes('low')) return 'success';
      if (lower.includes('moderate') || lower.includes('medium')) return 'warning';
      if (lower.includes('aggressive') || lower.includes('high')) return 'error';
      return 'default';
    }
    if (riskProfile <= 2) return 'success'; // Conservative
    if (riskProfile <= 3) return 'warning'; // Moderate
    return 'error'; // Aggressive
  };

  const getRiskLabel = (riskProfile: string | number | null): string => {
    if (!riskProfile) return 'N/A';
    if (typeof riskProfile === 'string') return riskProfile;
    if (riskProfile <= 2) return 'Conservative';
    if (riskProfile <= 3) return 'Moderate';
    return 'Aggressive';
  };

  if (!portfolios || portfolios.length === 0) {
    return (
      <div className="bg-gray-50 rounded-lg p-8 text-center">
        <Briefcase className="w-12 h-12 text-gray-400 mx-auto mb-3" />
        <p className="text-gray-600">No portfolios found for this client</p>
      </div>
    );
  }

  return (
    <div className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 ${className}`}>
      {portfolios.map((portfolio) => (
        <div
          key={portfolio.id}
          className={`
            bg-white border border-gray-200 rounded-lg p-5
            cursor-pointer hover:shadow-lg transition-all duration-200
            ${portfolio.alerts && portfolio.alerts.length > 0 ? 'border-yellow-400 bg-yellow-50/30' : ''}
          `}
          onClick={() => onPortfolioClick(portfolio.id)}
        >
          {/* Alert Banner */}
          {portfolio.alerts && portfolio.alerts.length > 0 && (
            <div className="flex items-center gap-2 mb-3 p-2 bg-yellow-100 rounded-md border border-yellow-300">
              <AlertCircle className="w-4 h-4 text-yellow-700 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                {portfolio.alerts.map((alert, idx) => (
                  <p key={idx} className="text-xs font-medium text-yellow-800 truncate">
                    {alert}
                  </p>
                ))}
              </div>
            </div>
          )}

          {/* Header */}
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center gap-3 flex-1 min-w-0">
              <div className="w-10 h-10 bg-[#002B45] bg-opacity-10 rounded-lg flex items-center justify-center flex-shrink-0">
                <Briefcase className="w-5 h-5 text-[#002B45]" />
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="font-semibold text-gray-900 truncate" title={portfolio.name}>
                  {portfolio.name}
                </h4>
                <p className="text-xs text-gray-500 mt-0.5">
                  {portfolio.mandateType}
                </p>
              </div>
            </div>
            <ChevronRight className="w-5 h-5 text-gray-400 flex-shrink-0 ml-2" />
          </div>

          {/* Value & Performance */}
          <div className="mb-4">
            <p className="text-xs text-gray-500 mb-1">Portfolio Value</p>
            <p className="text-2xl font-bold text-[#002B45]">
              {formatCurrency(portfolio.value)}
            </p>
            <div className="flex items-center justify-between mt-2">
              <span className="text-xs text-gray-500">
                {portfolio.percentOfClientAuM.toFixed(1)}% of total AuM
              </span>
              {portfolio.performance !== undefined && portfolio.performance !== null && (
                <TrendIndicator
                  value={portfolio.performance}
                  showIcon={true}
                  showValue={true}
                  className="text-sm"
                  suffix=" YTD"
                />
              )}
            </div>
          </div>

          {/* Footer: Risk & Accounts */}
          <div className="flex items-center justify-between pt-3 border-t border-gray-200">
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500">Risk:</span>
              <Badge
                label={getRiskLabel(portfolio.riskProfile)}
                variant={getRiskBadgeColor(portfolio.riskProfile)}
                size="sm"
              />
            </div>

            <div className="flex items-center gap-1 text-gray-600">
              <Users className="w-3.5 h-3.5" />
              <span className="text-xs">
                {portfolio.accountCount} {portfolio.accountCount === 1 ? 'account' : 'accounts'}
              </span>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default PortfolioBreakdown;
